import { Button, Icon, Modal } from '@distribusion/owl-frontend-ui'
import React, { ReactElement, useMemo, useState } from 'react'

import { POPUP_TYPES } from '@constants/popupTypes'
import { usePopupAnalytics } from '@hooks/usePopupAnalytics'
import { useTripTypes } from '@hooks/useTripTypes'
import amplitude from '@lib/analytics/amplitude'
import bem from '@lib/bem'
import fareClassUtils from '@lib/fareClass'
import { Trans, useTranslation } from '@lib/i18n'
import weeklyUtils from '@lib/weekly'
import ConnectionDetails from '@pages/Checkout/BookingDetails/TripDetails/ConnectionDetails'
import { useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'

import '@pages/Checkout/BookingDetails/TripDetails/index.scss'

interface TripDetailsProps {
  fareClass: string
  carriers: MarketingCarrier[]
  weeklyConnections?: WeeklyConnectionsParams | null
}

const TripDetails = ({ fareClass, carriers, weeklyConnections }: TripDetailsProps): ReactElement => {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(false)
  const [{ outbound, inbound }] = useCheckout()
  const [{ express }] = useParams()
  const weeklyTripsCount = weeklyUtils.getConnectionsCount(weeklyConnections)
  const { trackPopupClick } = usePopupAnalytics(POPUP_TYPES.CHECKOUT_TRIP_DETAILS, 'checkout', opened)

  const handleOpen = (): void => {
    setOpened(true)
    amplitude.checkout.clickTripDetails()
  }

  const resolvedFareClass: FareClass | undefined = useMemo(
    () => (fareClass ? fareClassUtils.getFareClassByCode(fareClass, carriers) : undefined),
    [fareClass, carriers],
  )

  const { isOpenReturnTrip, isLimitedOneTimeTrip, isDailyPassTrip } = useTripTypes(resolvedFareClass)

  const hideTime = express || !!weeklyConnections || isOpenReturnTrip || isLimitedOneTimeTrip || isDailyPassTrip

  return (
    <>
      <div className={bem('trip-details')}>
        <div className={bem('trip-details', 'icon-container')}>
          <div className={bem('icon-container', 'icon')}>
            <Icon name="ticket-rotated" size="medium" isDecorative={true} />
          </div>
        </div>
        <div className={bem('trip-details', 'link-button-container')}>
          <Trans
            i18nKey="checkout.tripDetails.link"
            components={[
              <span key={0} />,
              <Button
                data-tag="check-trip-details-button"
                key={1}
                buttonType="button"
                onClick={handleOpen}
                variant="text"
                fullWidth={false}
                className={bem('trip-details', 'inline-button')}
              >
                &nbsp;
              </Button>,
            ]}
          />
        </div>
      </div>

      <Modal
        opened={opened}
        onClose={() => {
          trackPopupClick('close')
          setOpened(false)
        }}
        title={t('checkout.tripDetails.title')}
        fullScreen
      >
        <div className={bem('p-6')}>
          {outbound && (
            <ConnectionDetails
              connection={outbound}
              type="outbound"
              fareClass={fareClass}
              carriers={carriers}
              hideTime={hideTime}
              weeklyConnections={weeklyConnections}
            />
          )}
          {inbound && !weeklyTripsCount && (
            <ConnectionDetails
              connection={inbound}
              type="inbound"
              fareClass={fareClass}
              carriers={carriers}
              hideTime={hideTime}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default TripDetails
